import { Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, map, skipWhile, take, takeUntil } from 'rxjs/operators';

import { MarketingFacade } from '../../../facades/marketing.facade';
import { ICart, ICartUpdateItemData } from '../../../models/cart.models';
import { AppUtils } from '../../../utils/app.utils';
import { CartUtils } from '../../../utils/cart.utils';
import { State } from '../../../reducers';
import { DataSharedFacade } from '../../../facades/data-shared.facade';
import { IContractParams } from '../../../models/catalog.models';
import { IconType } from '../../../models/settings.model';
import { CpqFacade } from '../../../facades/cpq.facade';
import { ITotals } from '../../../models/common.models';
import { MathUtils } from '../../../utils/math.utils';
import {
  EFeatureToggles,
  EMultiCartTabs,
  EStoreFeatures,
  EStoreTypes,
  EUserRoles,
} from '../../../configurations/common';
import { CatalogActions, CustomerActions, WishlistActions } from '../../../actions';
import * as WishlistSelector from '../../../reducers/wishlist.reducer';
import * as ShopCartActions from '../../../actions/shop-cart.actions';
import * as ShopCartSelectors from '../../../reducers/shop-cart.reducer';
import { FileType, FileUtils } from '../../../utils/file.utils';
import { CheckoutFacade } from '../../../facades/checkout.facade';
import { PageTypes } from '../../../analytics/enums/pageTypes';
import { AnalyticsService } from '../../../analytics/analytics.service';
import { CustomerFacade } from '../../../facades/customer.facade';
import { StoreConfigurationFacade } from '../../../facades/store-configuration.facade';
import { ConfigurationFacade } from '../../../facades/configuration.facade';
import { HttpResponse } from '@angular/common/http';
import { CurrentCartComponent } from './current-cart/current-cart.component';

@Component({
  selector: 'app-multi-cart',
  templateUrl: './multi-cart.component.html',
  styleUrls: ['./multi-cart.component.scss'],
  standalone: false,
})
export class MultiCartComponent implements OnInit, OnDestroy {
  private router: Router = inject(Router);
  private store: Store<State> = inject<Store<State>>(Store);
  private cpqFacade: CpqFacade = inject(CpqFacade);
  private marketingFacade: MarketingFacade = inject(MarketingFacade);
  private customerFacade: CustomerFacade = inject(CustomerFacade);
  private dataSharedFacade: DataSharedFacade = inject(DataSharedFacade);
  private checkoutFacade: CheckoutFacade = inject(CheckoutFacade);
  private configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private storeConfigurationFacade: StoreConfigurationFacade = inject(StoreConfigurationFacade);
  private analyticsService: AnalyticsService = inject(AnalyticsService);

  @ViewChild('currentCartRef') currentCartRef!: CurrentCartComponent;
  @Input() isExportCartActive!: boolean;

  isUsStore: boolean = false;
  isCaStore: boolean = false;
  isJpStore: boolean = false;
  isAuStore: boolean = false;
  rfqActive: boolean = false;
  isRfqOnly: boolean = false;
  isCheckoutQuoteApprovalMandatory: boolean = false;
  isBusinessPartnerRole: boolean = false;

  isCartOperationInProgress: boolean = false;
  switchingCartInProgress: boolean = false;

  addWishlistModalActive: boolean = false;
  addNicknameModalActive: boolean = false;
  deleteCartModalActive: boolean = false;
  deleteQuoteModalActive: boolean = false;
  removeConfigurationModalActive: boolean = false;

  hasNickname: boolean = false;
  currentCartLoaded: boolean = false;
  currentCartName$ = new ReplaySubject<string>();
  currentCart!: ICart;
  currentCartItems: any[] = [];
  cartIdOfSwitchingCart!: string;
  cartIdOfCartWhichIsPrepareToDelete!: string;
  quoteIdOfQuoteWhichIsPrepareToDelete!: string;
  minimumOrderValue: number = 0;

  otherCarts: any[] = [];
  otherCartsImages: any[] = [];
  otherPartProductCarts: any[] = [];
  otherServiceProductCarts: any[] = [];

  isQuantityValid: boolean = true;
  updateItemInCartData: any;
  updateItemInCartData$!: Observable<ICartUpdateItemData>;
  quoteItemToDelete!: {
    sku: string;
    configId: string;
  };

  wishlists: any[] = [];
  isWishlistsActive: boolean = this.configurationFacade.isFeatureAvailable(EStoreFeatures.WISHLISTS);
  isProductTagsActive: boolean = this.configurationFacade.isFeatureAvailable(EStoreFeatures.PRODUCT_TAGS);
  totals!: ITotals;
  currency!: string;
  validUntilDate!: string;

  viewAllPartsCarts: boolean = false;
  viewAllServicesQuotes: boolean = false;
  viewItemsForParts!: number;
  viewItemsForServices!: number;

  iconType = IconType;
  multiCartTabs = EMultiCartTabs;
  currentTab: string = EMultiCartTabs.PARTS;
  countOfCarts!: number;
  countOfOtherCarts: number = 0;
  currentCartLoaded$: Observable<boolean> = new Observable<boolean>();
  companyRoles: EUserRoles[] = [];
  isRfqFlowEnabled: boolean = false;
  isMyInstalledBaseFlowEnabled: boolean = false;
  isHarmonizedCheckoutProcessEnabled: boolean = false;
  isApproverBuyerWorkflowEnabled: boolean = false;
  isPredefinedEquipmentFlowEnabled$: Observable<boolean> = this.configurationFacade.isFeatureEnabled(EFeatureToggles.PREDEFINED_EQUIPMENT_FLOW);

  private unsubscribe$ = new Subject<void>();
  private initWidth: number = 0;
  private pageReadyEventFired: boolean = false;
  private loadingCartDataInProgress: boolean = false;
  private isRequiredToResetDeliveryPagesFields: boolean = true;

  onResize(event: Event): void {
    const innerWidth = (event.target as Window).innerWidth;
    this.calculateNumberOfItems(innerWidth);
  }

  ngOnInit(): void {
    this.isUsStore = AppUtils.isStoreActive(EStoreTypes.US);
    this.isCaStore = AppUtils.isStoreActive(EStoreTypes.CA);
    this.isJpStore = AppUtils.isStoreActive(EStoreTypes.JP);
    this.isAuStore = AppUtils.isStoreActive(EStoreTypes.AU);
    this.currentCartLoaded$ = this.marketingFacade.selectLoadingCartDataInProgress();
    this._selectIsRfqFlowEnabled();
    this.selectIsMyInstalledBaseFlowEnabled();
    this.getCompanyRoles();
    this.selectRfqActive();
    this.selectIsRfqOnly();
    this._selectIsHarmonizedCheckoutProcessEnabled();
    this._selectIsApproverBuyerFlowEnabled();
    this.selectIsQuoteApprovalForCheckoutRequired();
    this.selectMinimumOrderValue();
    this.selectCartOperationInProgress();
    this.selectCurrentCart();
    this.selectIsBPRole();
    this.loadOtherCarts();
    this.calculateNumberOfItems(window.innerWidth);
    this.store.dispatch(WishlistActions.retrieveWishlists());
    this.getCountOfCarts();
    this.getWishlistsFromStore();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getWishlistsFromStore(): void {
    this.store.select(WishlistSelector.getWishlists).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(wishlists => {
      this.wishlists = wishlists;
    });
  }

  /**
   * This method get count of carts from store.
   */
  getCountOfCarts(): void {
    this.marketingFacade.selectCarts()
      .pipe()
      .subscribe(carts => {
        this.countOfCarts = carts.length;
      });
  }

  createNewEmptyCartAndRedirectTo(): void {
    if (this.switchingCartInProgress) {
      return;
    }
    this.switchingCartInProgress = true;
    this.clearDeliveryPagesData();
    this.marketingFacade.createEmptyCart();
    this.store.dispatch(CatalogActions.setCachedProductsToBeReloaded());
    combineLatest([
      this.marketingFacade.selectCart().pipe(
        skipWhile(cart => !cart || cart.id === this.currentCart.id),
        take(1),
      ),
      this.isPredefinedEquipmentFlowEnabled$.pipe(take(1)),
    ]).subscribe(([cart, isPredefinedEquipmentFlowEnabled]) => {
      const cartId = cart.id;
      let redirect;

      if (this.isBusinessPartnerRole && !isPredefinedEquipmentFlowEnabled) {
        redirect = '/equipment-selection';
      } else {
        redirect = this.isMyInstalledBaseFlowEnabled ? '/my-installed-base' : '/catalog/parts';
      }

      if (cart.attributes.hasContractInCart) {
        this.redirectWithParam(redirect, cartId);
      } else {
        this.router.navigate([redirect]);
      }
    });
  }

  deleteOtherCart(data: any): void {
    const {cartId, totalItemsQuantity} = data;
    this.cartIdOfCartWhichIsPrepareToDelete = cartId;

    if (totalItemsQuantity === 0) {
      this.marketingFacade.deleteCart(this.cartIdOfCartWhichIsPrepareToDelete);

      if (!this.currentCart.id) {
        this.marketingFacade.createEmptyCart();
      }
    } else {
      this.isRequiredToResetDeliveryPagesFields = false;
      this.deleteCartModalActive = true;
    }
  }

  deleteOtherQuote(data: any): void {
    const {cartId, totalItemsQuantity} = data;
    this.quoteIdOfQuoteWhichIsPrepareToDelete = cartId;

    if (totalItemsQuantity === 0) {
      this.marketingFacade.deleteCpqCart(this.quoteIdOfQuoteWhichIsPrepareToDelete);

      if (!this.currentCart.id) {
        this.marketingFacade.createEmptyCart();
      }
    } else {
      this.deleteQuoteModalActive = true;
    }
  }

  deleteCurrentCart(modalDeleteActive: boolean): void {
    this.deleteCartModalActive = modalDeleteActive;
    this.cartIdOfCartWhichIsPrepareToDelete = this.currentCart.id;
  }

  deleteCurrentQuote(modalDeleteActive: boolean): void {
    this.deleteQuoteModalActive = modalDeleteActive;
    this.quoteIdOfQuoteWhichIsPrepareToDelete = this.currentCart.id;
  }

  deleteCart(): void {
    //check if is the "delete" function triggered by button in the current cart component or in the other cart component
    this.getCountOfCarts();
    if (this.isRequiredToResetDeliveryPagesFields) {
      this.clearDeliveryPagesData();
    }
    this.isRequiredToResetDeliveryPagesFields = true;

    this.marketingFacade.deleteCart(this.cartIdOfCartWhichIsPrepareToDelete);
    this._createEmptyCartIfNeeded();

    this.currentCartLoaded = this.countOfCarts > 1;
    this.cartIdOfCartWhichIsPrepareToDelete = '';
  }

  deleteQuote(): void {
    this.quoteIdOfQuoteWhichIsPrepareToDelete = this.currentCart.id;
    this.marketingFacade.deleteCpqCart(this.quoteIdOfQuoteWhichIsPrepareToDelete);
    this._createEmptyCartIfNeeded();

    this.currentCartLoaded = false;
    this.quoteIdOfQuoteWhichIsPrepareToDelete = '';
  }

  removeCartItem(itemId: string): void {
    if (this.isQuantityValid) {
      this.store.dispatch(ShopCartActions.deleteItemFromCart({
        cartId: this.currentCart.id,
        itemId: itemId,
      }));
    }
  }

  removeQuoteItem(): void {
    this.isCartOperationInProgress = true;

    this.cpqFacade.deleteCpqQuoteItem(this.currentCart.id, this.quoteItemToDelete.sku, this.quoteItemToDelete.configId)
      .pipe(take(1)).subscribe({
      next: () => {
        this.cpqFacade.postCpqRecalculate(this.currentCart.id).pipe(take(1)).subscribe({
          next: () => this.marketingFacade.refreshCart(this.currentCart.id),
          error: () => this.isCartOperationInProgress = false,
        });
      },
      complete: () => this.isCartOperationInProgress = false,
    });
  }

  updateCartItemQuantity(data: any): void {
    const {oldQuantity, newQuantity, itemId, itemName} = data;
    const updatedQty = parseInt(newQuantity, 10);

    if (!MathUtils.checkIfNumeric(updatedQty)) {
      this.isQuantityValid = false;
      return;
    }

    this.isQuantityValid = true;

    const quantityDiff = +newQuantity - +oldQuantity;
    if (quantityDiff > 0) {
      this.trackIncreaseQuantity(itemId, itemName, quantityDiff);
    } else if (quantityDiff < 0) {
      this.trackDecreaseQuantity(itemId, itemName, -quantityDiff);
    }

    this.store.dispatch(ShopCartActions.setUpdateItemInCartData({quantity: updatedQty}));
    this.updateItemInCartData$ = this.store.select(ShopCartSelectors.getUpdateItemInCartData);
    this.updateItemInCartData$.pipe(takeUntil(this.unsubscribe$)).subscribe(itemData => this.updateItemInCartData = itemData);

    this.store.dispatch(ShopCartActions.updateItemInCartLoadCartItems({
      cartId: this.currentCart.id,
      itemId: itemId,
      updateItemInCartData: this.updateItemInCartData,
    }));
  }

  switchDefaultCart(data: any): void {
    this.switchingCartInProgress = true;

    const {cartId, redirect} = data;
    this.cartIdOfSwitchingCart = cartId;

    this.marketingFacade.switchDefaultCart(cartId);
    this.marketingFacade.selectCart().pipe(
      skipWhile(cart => cart?.id !== cartId),
      take(1),
    ).subscribe({
      next: cart => {
        if (redirect) {
          if (cart.attributes.hasContractInCart) {
            this.redirectWithParam('/quote-summary', cartId);
          } else {
            this.marketingFacade.selectCartItemsWithDetails().pipe(
              takeUntil(this.unsubscribe$),
            ).subscribe(items => {
              this.router.navigate([this.getCheckoutRoute(items)]);
            });
          }
        }
        this.switchingCartInProgress = false;
      },
      error: () => this.switchingCartInProgress = false,
    });
  }

  private getCheckoutRoute(items: any): string {
    const harmonizedCheckoutRouteExtension: string = this.isHarmonizedCheckoutProcessEnabled ? '/checkout' : '';

    if (AppUtils.isSapStore() && !this.isHarmonizedCheckoutProcessEnabled) {
      return '/sap-delivery-details';
    }

    return ((this.rfqActive && !this.isRfqFlowInactive(items)) || this.isRfqOnly)
      ? `${harmonizedCheckoutRouteExtension}/request-quote`
      : `${harmonizedCheckoutRouteExtension}/delivery-details`;
  }

  private isRfqFlowInactive(items: any): boolean {
    return !this.isRfqFlowEnabled
      || CartUtils.isSetPricesForAllItems(items);
  }

  buildServiceParams(cartItem: any): IContractParams {
    return {
      'fl-number': cartItem.attributes.systemDetails.siemensEquipmentId,
      'rel-product-sysivk': cartItem.attributes.systemDetails.materialNumber,
      selectedService: cartItem.attributes.abstractSku,
      configId: cartItem.attributes.cpqConfigId,
    };
  }

  selectRfqActive(): void {
    this.marketingFacade.selectRfqActive().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      this.rfqActive = state;
    });
  }

  selectIsQuoteApprovalForCheckoutRequired(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.CHECKOUT_QUOTE_APPROVAL_MANDATORY).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      this.isCheckoutQuoteApprovalMandatory = state;
    });
  }

  selectIsBPRole(): void {
    this.customerFacade.isBusinessPartner().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isBpRole => {
      this.isBusinessPartnerRole = isBpRole;
    });
  }

  isContractInCart(): boolean {
    return this.currentCart?.attributes.hasContractInCart ?? false;
  }

  generatePdfFile(): void {
    this.checkoutFacade.getCartDetailPdfFile(this.currentCart.id).pipe(take(1))
      .subscribe((response: HttpResponse<Blob>) => {
        const contentDispositionHeader: string = response.headers.get('Content-Disposition') ?? '';
        const filename: string = FileUtils.extractFilenameFromContentDisposition(contentDispositionHeader, 'Cart_details' + this.currentCart.id);
        if (response.body) {
          FileUtils.saveAndOpenFile(response.body, FileType.PDF, filename);
        }
        this.currentCartRef.fileGenerationInProgress = false;
      });
  }

  private selectCurrentCart(): void {
    combineLatest([
      this.marketingFacade.selectCart(),
      this.marketingFacade.selectCartItemsWithDetails(),
      this.marketingFacade.getCurrentCartItems(),
    ]).pipe(
      takeUntil(this.unsubscribe$),
      map(([cart, cartItemsWithDetails, cartItems]) => ({cart, cartItemsWithDetails, cartItems}),
      )).pipe(debounceTime(0)).subscribe(data => {
      this.loadingCartDataInProgress = false;
      if (data.cart) {
        this.analyticsService.setProducts(data.cartItemsWithDetails);
        this.trackPageReady();
        this.currency = data.cart.attributes.currency;
        this.currentCart = data.cart;
        this.currentCartItems = data.cartItemsWithDetails;
        this.totals = data.cart.attributes.totals;
        this.validUntilDate = data.cart.attributes.validUntil ? formatDate(data.cart.attributes.validUntil, 'MM/dd/yyyy', 'en') : '';
        this.hasNickname = !MathUtils.checkIfNumeric(this.currentCart.attributes?.name);
        this.currentCartName$.next(this.hasNickname ? this.currentCart.attributes.name : '');
        this.currentCartItems = CartUtils.mapAvailabilitiesToCartItems(
          this.currentCartItems, data.cartItems?.included ?? [],
        );
        this.cartIdOfCartWhichIsPrepareToDelete = this.currentCart.id;
        this.quoteIdOfQuoteWhichIsPrepareToDelete = this.currentCart.id;
        this.currentCartLoaded = true;

        if (this.isContractInCart() && this.otherServiceProductCarts?.length > 0) {
          this.currentTab = EMultiCartTabs.SERVICES;
        } else {
          this.currentTab = EMultiCartTabs.PARTS;
        }
      }
    });
  }

  private getCompanyRoles(): void {
    this.customerFacade.getCustomerCompanyRoles().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(companyRoles => {
      this.companyRoles = companyRoles;
    });
  }

  /**
   * Creates a new empty cart when the approver-buyer workflow requires it
   * (no approver assigned, workflow enabled, user has no approver access and no other carts exist).
   *
   * @private
   */
  private _createEmptyCartIfNeeded(): void {
    if (
      !this.currentCart.attributes.approverDetails.approverId
      && this.isApproverBuyerWorkflowEnabled
      && !this._hasApproverAccess()
      && this.countOfOtherCarts === 0
    ) {
      this.marketingFacade.createEmptyCart();
    }
  }

  /**
   * Checks whether the current user has approver-level access based on their company roles.
   *
   * @returns {boolean} true if the user has Approver, Admin or BusinessPartnerApprover role
   * @private
   */
  private _hasApproverAccess(): boolean {
    return this.companyRoles.some(role => [
      EUserRoles.Approver,
      EUserRoles.BusinessPartnerApprover,
      EUserRoles.Admin,
    ].includes(role));
  }

  private loadOtherCarts(): void {
    this.marketingFacade.selectCarts().pipe(
      skipWhile(val => val.length === 0),
      takeUntil(this.unsubscribe$),
    ).subscribe(carts => {
      if (!carts) {
        this.otherCarts = [];
        return;
      }

      this.otherCarts = [...carts.filter(c => !c.attributes.isDefault)].map(data => ({...data, itemDetails: []}));

      if (this.isRfqOnly) {
        this.otherCarts = this.otherCarts.filter(cart => !cart.attributes.pointOfContact).map(data => data);
      }

      this.countOfOtherCarts = this.otherCarts.length;

      this.marketingFacade.selectAllCartsItems().pipe(take(1)).subscribe(items => {
        const partProductCartsSet = new Set();
        const serviceProductCartsSet = new Set();
        const includedItems = items?.filter((include: any) => include.type === 'items') ?? [];

        this.otherCartsImages = items?.filter((i: any) => i.type === 'concrete-product-image-sets');

        this.otherCarts.forEach(cart => {
          cart.relationships?.items?.data.forEach((data: any) =>
            cart.itemDetails.push(...includedItems.filter((item: any) => item.id === data.id)),
          );

          if (!cart.attributes.hasContractInCart) {
            partProductCartsSet.add(cart);
          } else {
            serviceProductCartsSet.add(cart);
          }
        });

        this.otherPartProductCarts = [...partProductCartsSet];
        this.otherServiceProductCarts = [...serviceProductCartsSet];
      });
    });
  }

  private selectCartOperationInProgress(): void {
    this.marketingFacade.selectCartOperationInProgress().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(inProgress => {
      this.isCartOperationInProgress = inProgress;
    });
  }

  private redirectWithParam(path: string, cartId?: string, item?: any): void {
    let cartItem;

    if (!cartId) {
      cartId = this.currentCart.id;
      cartItem = item || this.currentCartItems[0];
    } else {
      cartItem = this.otherCarts.find(cart => cart.id === cartId)?.itemDetails[0];
    }

    const serviceParams = this.buildServiceParams(cartItem);
    this.dataSharedFacade.setCartId(cartId);
    this.router.navigate([path], {queryParams: serviceParams}).then();
  }

  private calculateNumberOfItems(innerWidth: number): void {
    if (this.initWidth !== innerWidth) {
      this.initWidth = innerWidth;
      this.viewItemsForParts = this.initWidth < 576 ? 2 : 3;
      this.viewItemsForServices = this.initWidth < 576 ? 2 : 4;
    }
  }

  private trackPageReady(): void {
    if (!this.pageReadyEventFired && !this.loadingCartDataInProgress) {
      this.analyticsService.trackPageReady('Shopping Cart Page', PageTypes.SHOPPING_CART_PAGE, 'shopping-cart');
      this.pageReadyEventFired = true;
    }
  }

  private trackIncreaseQuantity(itemId: string, itemName: string, quantityDiff: number): void {
    this.analyticsService.setProducts({
      sku: itemId,
      name: itemName,
      quantity: quantityDiff,
    });

    this.analyticsService.trackCart('cart.add');
  }

  private trackDecreaseQuantity(itemId: string, itemName: string, quantityDiff: number): void {
    this.analyticsService.setProducts({
      sku: itemId,
      name: itemName,
      quantity: quantityDiff,
    });

    this.analyticsService.trackCart('cart.remove');
  }

  clearDeliveryPagesData(): void {
    this.store.dispatch(CustomerActions.clearCustomShipToAddress());
    this.store.dispatch(CustomerActions.clearPointOfContact());
    this.store.dispatch(ShopCartActions.clearPriceDisputing());
  }

  /**
   * Method to select if only Rfq workflow is enabled for current store
   * @private
   */
  private selectIsRfqOnly(): void {
    this.storeConfigurationFacade.selectIsRfqOnly().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      this.isRfqOnly = state;
    });
  }

  /**
   * Method to select minimal order value for current store
   * @private
   */
  private selectMinimumOrderValue(): void {
    this.storeConfigurationFacade.selectMinimumOrderValue().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      this.minimumOrderValue = state;
    });
  }

  /**
   * Method for retrieving IsMyInstalledBaseFlowEnabled feature toggle value
   * @private
   */
  private selectIsMyInstalledBaseFlowEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.MY_INSTALLED_BASE_FLOW).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isMyInstalledBaseFlowEnabled => {
      this.isMyInstalledBaseFlowEnabled = isMyInstalledBaseFlowEnabled;
    });
  }

  /**
   * Method to select "harmonized checkout process" feature toggle value.
   *
   * @private
   */
  private _selectIsHarmonizedCheckoutProcessEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.HARMONIZED_DELIVERY_DETAILS_PAGE).pipe(take(1)).subscribe(value => {
      this.isHarmonizedCheckoutProcessEnabled = value;
    });
  }

  /**
   * Method to select "approver buyer flow" feature toggle value.
   *
   * @private
   */
  private _selectIsApproverBuyerFlowEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.APPROVER_BUYER_FLOW).pipe(take(1)).subscribe(value => {
      this.isApproverBuyerWorkflowEnabled = value;
    });
  }

  /**
   * Method for retrieving rfq_flow feature toggle value.
   * @private
   */
  private _selectIsRfqFlowEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.RFQ_FLOW).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isRfqFlowEnabled => {
      this.isRfqFlowEnabled = isRfqFlowEnabled;
    });
  }
}
